import { isEqual } from "lodash"
import { DependencyList, EffectCallback, ForwardedRef, useEffect, useRef } from "react"
import {
    Article,
    ArticleAttribute,
    OE,
    OeArticleDto,
    OeArticleQuantitiesRequest,
    SupplierArticleQuantitiesRequest,
    ListFilter,
    WholesalerArticleQuantitiesRequest,
    WholesalerPart,
    WholesalerArticleDto,
    channel,
    ConfigParams,
    ArticleErpInfos,
    WholesalerArticle,
    SupplierArticle,
    ProductGroup,
    ProvidedArticles,
    ArticleListType,
    UserModuleType,
    UserContext,
    CrossSellingModalType,
    TmaEModule,
    ECrossSellingType,
} from "@tm/models"
import Morpheus from "@tm/morpheus"
import { getParameterFromUserContextModule } from "@tm/context-distribution"
import { findIndexOrFirst } from "@tm/utils"
import { ArticleGroupParams } from "../models"
import { DataSupplierFilter, ProductGroupFilter } from "../../../data/model"
import { CriterionFilter } from "../../../data/model/uni-parts"

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const emptyFunc = () => {}

export function getArticleGroups(articles: Article[], articlesIncomplete: boolean) {
    const groups: ArticleGroupParams[] = []

    articles.forEach((article) => {
        const lastGroup = groups.last()

        if (lastGroup && lastGroup.productGroup.id === article.productGroup.id) {
            lastGroup.articles.push(article)
        } else {
            groups.push({
                type: "default",
                productGroup: article.productGroup,
                articles: [article],
                incomplete: false, // Assume the group is complete
            })
        }
    })

    // Only the last group can be incomplete
    if (articlesIncomplete && groups.length) {
        groups[groups.length - 1].incomplete = true
    }

    return groups
}

export function getArticleGroupsWithExactMatches(articles: Article[], articlesIncomplete: boolean) {
    const exactMatch: Article[] = []
    const other: Article[] = []

    articles.forEach((article) => {
        if (article.isExactMatch) {
            exactMatch.push(article)
        } else {
            other.push(article)
        }
    })

    return {
        exactMatch: getArticleGroups(exactMatch, articlesIncomplete),
        other: getArticleGroups(other, articlesIncomplete),
    }
}

export function getProvidedArticlesGroup(articles: Article[] | undefined, articlesIncomplete: boolean, groupTitle: string) {
    if (groupTitle && articles) {
        return {
            type: "default",
            productGroup: {
                id: 0,
                name: groupTitle,
                thumbnail: "",
                sortId: 0,
            },
            articles,
            incomplete: articlesIncomplete,
        } as ArticleGroupParams
    }

    return undefined
}

export function mapDistinctValues<T, K = keyof T>(items: T[], selector: (item: T) => K) {
    return items.reduce<K[]>((prev, curr) => {
        const val = selector(curr)

        if (prev.some((x) => x === val)) {
            return prev
        }

        return [...prev, val]
    }, [])
}

/**
 * Checks if all items in source are found in object. It perfoms a deep comparison. The order of the items doesn't matter.
 * @param object
 * @param source
 * @returns
 */
export function isPartOf(object: unknown[], source: unknown[]) {
    for (let s = 0; s < source.length; s++) {
        let found = false

        for (let o = 0; o < object.length; o++) {
            if (isEqual(source[s], object[o])) {
                found = true
                break
            }
        }

        if (!found) {
            return false
        }
    }

    return true
}

export function objectValuesEqual(obj1: any, obj2: any) {
    if (!obj1 || !obj2) {
        return false
    }

    const keys = Object.keys(obj1)

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i]

        if (typeof obj1[key] === "object" && !objectValuesEqual(obj1[key], obj2[key])) {
            return false
        }

        if (obj1[key] !== obj2[key]) {
            return false
        }
    }

    return true
}

export function useForwardedRef<T>(ref: ForwardedRef<T>, initialValue: T | null = null) {
    const targetRef = useRef<T>(initialValue)

    useEffect(() => {
        if (!ref) {
            return
        }

        if (typeof ref === "function") {
            ref(targetRef.current)
        } else {
            ref.current = targetRef.current
        }
    }, [ref])

    return targetRef
}

export function mapProductGroupFilter(filter: ProductGroupFilter, isExtendedAssortmentEnabled: boolean): ListFilter {
    const mappedFilter: ListFilter = {
        ...filter,
        key: `${filter.id}`,
        showOnTop: filter.showOnTop || filter.hasTopPrioritySuppliers,
        isTopPriority: filter.isTopPriority || filter.hasTopPrioritySuppliers,
    }

    if (!isExtendedAssortmentEnabled && filter.showOnTop) {
        mappedFilter.articleCount = filter.topPriorityArticleCount
    }

    return mappedFilter
}

export function mapDataSupplierFilterAndShowOnTop(filter: DataSupplierFilter): ListFilter {
    const key = `${filter.id}`
    return {
        ...filter,
        showOnTop: filter.showOnTop || filter.isTopPriority,
        key,
    }
}

export function mapDataSupplierFilter(filter: DataSupplierFilter): ListFilter {
    const key = `${filter.id}`
    return {
        ...filter,
        key,
    }
}

export function mapArticleAttribute(filter: ArticleAttribute): ListFilter {
    const query = `${filter.id}|${filter.key || filter.value}`
    return {
        ...filter,
        query,
        priority: 1,
    }
}

export function mapCriterionFilter(filter: CriterionFilter): ListFilter {
    const query = `${filter.id}|${filter.valueKey || filter.value}`
    return {
        ...filter,
        query,
        priority: 1,
    }
}

export function mapListFilterToQuery(filter: ListFilter): string {
    // TODO: we need to clean up the filter structure in the backend
    return `${filter.id}|${filter.valueKey || filter.key || filter.value}`
}

export function mapSupplierArticleQuantitiesRequest(article: Article, workTaskId: string): SupplierArticleQuantitiesRequest {
    return {
        article: {
            dataSupplierArticleNumber: article.supplierArticleNo,
            dataSupplierId: article.supplier.id,
            productGroupId: article.productGroup.id,
        },
        workTaskId,
    }
}

export function mapOeArticleDtoFromOePart(part: OE.OePart): OeArticleDto {
    return {
        oeArticleNumber: part.number,
        productGroupId: part.productGroupId,
        vehicleManufacturerId: part.manufacturerId,
    }
}

export function mapWholesaleDtoFromWholesalerArticle(part: WholesalerArticle | WholesalerPart): WholesalerArticleDto {
    return {
        wholesalerArticleNumber: part.wholesalerArticleNumber,
    }
}

export function isSameOeArticle(a: OeArticleDto, b: OeArticleDto) {
    // There are cases where productGroupId is sometimes undefined, this does not work with Lodash isEqual
    return a.oeArticleNumber === b.oeArticleNumber && a.productGroupId == b.productGroupId && a.vehicleManufacturerId == b.vehicleManufacturerId
}

export function isSameWholesalerArticle(a: WholesalerArticleDto, b: WholesalerArticleDto) {
    return a.wholesalerArticleNumber === b.wholesalerArticleNumber
}

export function mapOeArticleQuantitiesRequest(article: OE.OePart, workTaskId: string): OeArticleQuantitiesRequest {
    return {
        article: mapOeArticleDtoFromOePart(article),
        workTaskId,
    }
}

export function mapWholesalerPartQuantitiesRequest(article: WholesalerPart, workTaskId: string): WholesalerArticleQuantitiesRequest {
    return {
        article: mapWholesaleDtoFromWholesalerArticle(article),
        workTaskId,
    }
}

export function mapWholesalerArticleQuantitiesRequest(article: WholesalerArticle, workTaskId: string): WholesalerArticleQuantitiesRequest {
    return {
        article: mapWholesaleDtoFromWholesalerArticle(article),
        workTaskId,
    }
}

export function mapWholesalerArticleToArticle(part: WholesalerArticle): Article {
    const description = part.additionalInformationExtended ?? ""
    const internalId = (description + part.wholesalerArticleNumber).hashCode()
    const quantity = part.quantity?.confirmedValue || part.quantity?.value || 1

    return {
        id: part.itemId,
        internalId,
        supplier: {
            id: undefined,
        } as unknown as SupplierArticle,
        productGroup: {
            id: undefined,
        } as unknown as ProductGroup,
        traderArticleNo: part.wholesalerArticleNumber,
        description,
        quantity,
        requestErpInfo: true,
    } as unknown as Article
}

export function publishModuleInfo(view: string, info: string) {
    const unsub = channel("GLOBAL").subscribe(
        "WORKTASK/ID_CHANGED",
        (data) => {
            if (data.id) {
                channel("WORKTASK", data.id).publish("MODULE/CHANGED", { id: "parts", name: "{{99}}", view, info })
                unsub()
            }
        },
        true
    )
}

export function ensureContextAvailability<T>(displayName?: string, context?: T): T {
    if (!context) {
        throw `Missing Context: ${displayName}`
    }

    return context
}

export function useAfterPaintEffect(effect: EffectCallback, dependencies: DependencyList) {
    useEffect(() => {
        requestAnimationFrame(() => {
            setTimeout(() => {
                effect()
            }, 0)
        })
    }, dependencies)
}

export function getOePositionsFromCache(cacheId: string, query?: string): OE.OePosition[] {
    let oePositions: OE.OePosition[] = []
    if (cacheId) {
        const stringValue = sessionStorage.getItem(cacheId)
        if (stringValue) {
            oePositions = JSON.parse(stringValue) ?? []

            if (!!oePositions.length && !oePositions.some((x) => x.isSelected)) {
                const index = findIndexOrFirst(oePositions, (x) => x.number === query)
                oePositions[index].isSelected = true
            }
        }
    }

    return oePositions
}

export function getProvidedArticlesFromCache(cacheId: string | undefined): ProvidedArticles | undefined {
    if (cacheId) {
        try {
            let providedArticles
            const data = sessionStorage.getItem(cacheId)
            if (data) {
                providedArticles = JSON.parse(data)
            }

            if (providedArticles && typeof providedArticles === "object") {
                return providedArticles as ProvidedArticles
            }
        } catch {}
    }

    return undefined
}

export function isAddButtonDisabled(articleErpInfos: ArticleErpInfos, requestErpInfo?: boolean) {
    const { enableAddingButtonsAfterErp } = Morpheus.getParams<ConfigParams>()
    if (!enableAddingButtonsAfterErp) {
        return false
    }
    // check if there is at least 1 completed request (error or sucess) already
    if (articleErpInfos.numberOfCompletedRequests && articleErpInfos.numberOfCompletedRequests >= 1) {
        return false
    }
    const defaultErpInfosIsLoading = articleErpInfos.default?.state === "loading" || articleErpInfos.default?.state === "queued"
    const alternativeErpInfosIsLoading = articleErpInfos.alternative?.state === "loading" || articleErpInfos.alternative?.state === "queued"
    const notYetRequested = !!articleErpInfos.isNotRequestedYet && !!requestErpInfo
    return defaultErpInfosIsLoading || alternativeErpInfosIsLoading || notYetRequested
}

export function mapTileDisplayBehaviorSettingsKey(listType: ArticleListType, userContext: UserContext, requireMdmParameter?: boolean) {
    switch (listType) {
        case "universal": {
            return (
                (!requireMdmParameter || !!getParameterFromUserContextModule(userContext, UserModuleType.UnipartsPremium, "TileView")) &&
                "uniSearchDisplayBehavior"
            )
        }
        case "offers": {
            return (
                (!requireMdmParameter || !!getParameterFromUserContextModule(userContext, UserModuleType.Offers, "TileView")) &&
                "offersDisplayBehavior"
            )
        }
        default: {
            return false
        }
    }
}

export const determineTmaOriginByCrossSellingModal = (modalType: CrossSellingModalType | undefined): TmaEModule | undefined => {
    switch (modalType) {
        case ECrossSellingType.alsoBoughtProductGroups:
            return TmaEModule.CROSS_SELLING_PRODUCT_GROUP
        case ECrossSellingType.recommendedProductGroups:
            return TmaEModule.PRODUCT_RECOMMENDATION
        default:
            return undefined
    }
}
